// Spec 27 PR 2a — recursive widget renderer with state machine.
//
// Looks up the registered component by kind, hands it the
// framework-owned state + onAction, and renders. State transitions
// triggered by user actions are propagated to the parent via
// onAction; transitions driven by server events (submitting,
// succeeded, failed) arrive as `externalState`.
//
// PR 2a does not register any widgets, so the surface only ever
// renders the unknown-kind fallback. That's fine — it gives us a
// visible signal that the framework is mounted and the path from
// spec → render exists, ready for PR 2b's widgets.

import { useEffect, useState } from "react";

import {
	type ClientWidgetSpec,
	getClientWidget,
	type WidgetState,
} from "./registry";

interface WidgetSurfaceProps {
	widget: ClientWidgetSpec;
	/** Optional: when the server pushes a state transition for this
	 *  widget (submitting → succeeded, etc.), the parent passes it
	 *  in here. If provided, it overrides the surface's local state. */
	externalState?: WidgetState;
	/** Called when the widget emits an action. The parent decides
	 *  what to do (typically: post to the turn endpoint, then update
	 *  externalState when the server confirms). */
	onAction?: (
		widgetId: string,
		action: string,
		values?: unknown,
	) => void;
}

export function WidgetSurface({
	widget,
	externalState,
	onAction,
}: WidgetSurfaceProps) {
	const [localState, setLocalState] = useState<WidgetState>({ kind: "idle" });

	// When the widget id changes, reset local state (a new widget
	// has arrived in its place). The previous widget's state stays
	// in the parent timeline if it was committed.
	useEffect(() => {
		setLocalState({ kind: "idle" });
	}, [widget.id]);

	const state = externalState ?? localState;
	const def = getClientWidget(widget.kind);

	if (!def) {
		return (
			<div className="text-[11px] text-[var(--color-text-dim)] mono py-2 px-3">
				Unknown widget kind: <span className="opacity-70">{widget.kind}</span>
			</div>
		);
	}

	const Component = def.component;

	const handleAction = (action: string, values?: unknown) => {
		// Conventional names — widgets use these so the framework
		// can drive shared transitions consistently. Widgets are
		// free to emit other actions; those just bubble up.
		switch (action) {
			case "dirty":
				if (state.kind === "idle" || state.kind === "dirty") {
					setLocalState({ kind: "dirty", values });
				}
				break;
			case "reset":
				setLocalState({ kind: "idle" });
				break;
			default:
				// All other actions (confirm, cancel, pick, etc.) bubble
				// up; the surface treats them as external signals and
				// expects the parent to manage state.
				break;
		}
		onAction?.(widget.id, action, values);
	};

	return (
		<Component
			widgetId={widget.id}
			props={widget.props}
			state={state}
			onAction={handleAction}
		/>
	);
}
